import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import SchemaOrg from '../../components/feature/SchemaOrg';
import PriceSimulator from '../../components/feature/PriceSimulator';
import { useEffect } from 'react';

export default function TransportUrgentWeekendParisPage() {
  useEffect(() => {
    document.title = 'Transport Urgent Week-end Paris | Livraison Samedi Dimanche 24/7';
    
    const metaTags = [
      { name: 'description', content: 'Besoin d\'un transport urgent le week-end à Paris ? Speed Transport assure vos livraisons express samedi et dimanche, 24h/24. Coursier disponible immédiatement pour vos urgences.' },
      { name: 'keywords', content: 'transport week-end Paris, livraison samedi dimanche Paris, coursier week-end, transport urgent dimanche, livraison 24/7 Paris' },
      { name: 'geo.region', content: 'FR-75' },
      { name: 'geo.placename', content: 'Paris' },
      { name: 'geo.position', content: '48.8566;2.3522' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Transport Urgent Week-end Paris | Livraison Samedi Dimanche 24/7' },
      { name: 'twitter:description', content: 'Besoin d\'un transport urgent le week-end à Paris ? Speed Transport assure vos livraisons express samedi et dimanche, 24h/24.' }
    ];

    metaTags.forEach(({ name, content }) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://transporteurexpressidf.fr/blog/transport-urgent-weekend-paris';
  }, []);

  const relatedArticles = [
    {
      slug: 'livraison-dernier-kilometre-paris',
      title: 'Livraison dernier kilomètre à Paris : enjeux et solutions',
      image: 'https://public.readdy.ai/ai/img_res/d66cd7a726c20cc646e5285b2380e332.jpg'
    },
    {
      slug: 'choisir-transporteur-paris-guide',
      title: 'Comment choisir son transporteur à Paris : guide complet',
      image: 'https://readdy.ai/api/search-image?query=professional%20transport%20company%20selection%20guide%20Paris%20logistics%20comparison%20checklist%20business%20decision%20making%20modern%20office%20setting&width=400&height=250&seq=related5&orientation=landscape'
    },
    {
      slug: 'coursier-moto-vs-utilitaire',
      title: 'Coursier moto ou utilitaire : que choisir à Paris ?',
      image: 'https://readdy.ai/api/search-image?query=comparison%20between%20motorcycle%20courier%20and%20delivery%20van%20in%20Paris%20city%20traffic%20urban%20logistics%20professional%20transport%20services%20side%20by%20side%20modern%20vehicles%20efficient%20delivery&width=400&height=250&seq=related6&orientation=landscape'
    }
  ];

  return (
    <>
      <SchemaOrg 
        type="article" 
        data={{
          headline: 'Transport Urgent le Week-end à Paris : Comment ça Marche ?',
          datePublished: '2024-01-22',
          dateModified: '2024-01-22',
          author: 'Speed Transport Paris',
          description: 'Découvrez comment fonctionne le transport urgent le week-end à Paris. Service 24/7, tarifs, délais et solutions pour vos livraisons express samedi et dimanche.'
        }}
      />
      <Navbar />
      <WhatsAppButton phoneNumber="0631012910" />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-500 to-emerald-600 text-white py-16">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <nav className="mb-6">
              <ol className="flex items-center space-x-2 text-sm text-white/80">
                <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-white font-medium">Transport Urgent Week-end</li>
              </ol>
            </nav>
            <div className="max-w-4xl">
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full mb-4">
                Service 24/7
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Transport Urgent le Week-end à Paris : Comment ça Marche ?
              </h1>
              <div className="flex items-center space-x-6 text-white/90">
                <span className="flex items-center">
                  <i className="ri-calendar-line mr-2"></i>
                  22 janvier 2024
                </span>
                <span className="flex items-center">
                  <i className="ri-time-line mr-2"></i>
                  7 min de lecture
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="container mx-auto px-4 -mt-12 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="relative w-full h-96 rounded-xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=weekend%20urgent%20delivery%20service%20in%20Paris%20professional%20courier%20working%20on%20saturday%20and%20sunday%20emergency%20transport%20fast%20logistics%20modern%20urban%20setting%20motorcycle%20delivery%20clean%20background&width=1200&height=600&seq=hero-weekend&orientation=landscape"
                alt="Transport urgent week-end à Paris - Livraison samedi dimanche"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Une urgence le samedi ou le dimanche ? Pas de panique ! Le <strong>transport urgent week-end à Paris</strong> est une réalité avec Speed Transport. Contrairement aux idées reçues, il est tout à fait possible d'obtenir une <strong>livraison express samedi dimanche à Paris</strong> avec les mêmes garanties de qualité qu'en semaine. Découvrez comment fonctionne notre service 24/7 et pourquoi nous sommes le <strong>coursier week-end</strong> de référence à Paris.
              </p>
            </div>

            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <a href="#pourquoi-transport-weekend" id="pourquoi-transport-weekend" className="hover:text-teal-600 transition-colors">
                  Pourquoi avoir besoin d'un transport le week-end ?
                </a>
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Les besoins de <strong>transport urgent dimanche</strong> ou samedi sont plus fréquents qu'on ne le pense. Voici les situations les plus courantes où nos clients font appel à nos services :
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-hospital-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Urgences médicales</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Transport de médicaments, analyses médicales, équipements sanitaires entre établissements de santé ou vers des patients à domicile.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-store-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Commerce et retail</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Réapprovisionnement d'urgence pour les boutiques ouvertes le week-end, livraison de produits frais pour la restauration.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-calendar-event-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Événements</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Mariages, salons professionnels, concerts : transport de matériel, décoration, documents oubliés ou équipements de dernière minute.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-briefcase-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Urgences professionnelles</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Dossiers juridiques urgents, pièces détachées critiques, équipements informatiques pour dépannage d'urgence.
                  </p>
                </div>
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-information-line text-teal-600 mr-2"></i>
                  Bon à savoir
                </h3>
                <p className="text-gray-700">
                  Environ 35% de nos interventions le week-end concernent des urgences professionnelles. Les entreprises parisiennes ont compris qu'un <strong>coursier week-end</strong> fiable peut éviter des pertes financières importantes. Pour plus d'informations, consultez notre <Link to="/#faq" className="text-teal-600 font-semibold hover:text-teal-700">FAQ transporteur express Île-de-France</Link>.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <a href="#comment-ca-marche" id="comment-ca-marche" className="hover:text-teal-600 transition-colors">
                  Comment fonctionne notre service week-end ?
                </a>
              </h2>

              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Disponibilité 24h/24, 7j/7</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Notre équipe de <strong>coursiers week-end</strong> est opérationnelle en permanence. Que ce soit samedi matin à 6h ou dimanche soir à 23h, nous répondons présents. Notre centrale d'appel est joignable au <a href="tel:+33632410246" className="text-teal-600 font-semibold hover:text-teal-700">+33 6 32 41 02 46</a> sans interruption.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Contrairement à de nombreux concurrents qui ferment le week-end ou appliquent des délais rallongés, Speed Transport maintient le même niveau de service qu'en semaine.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Prise en charge rapide</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Pour une <strong>livraison samedi dimanche Paris</strong>, nos délais de prise en charge sont identiques à ceux de la semaine :
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                        <span className="text-gray-700"><strong>Express :</strong> Prise en charge sous 30 minutes</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                        <span className="text-gray-700"><strong>Standard :</strong> Prise en charge sous 2 heures</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                        <span className="text-gray-700"><strong>Planifié :</strong> Créneau horaire de votre choix</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Flotte dédiée week-end</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Nous disposons d'une flotte spécifiquement affectée aux interventions du week-end :
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <i className="ri-motorbike-line text-teal-600 text-2xl mr-3"></i>
                          <strong className="text-gray-900">Motos</strong>
                        </div>
                        <p className="text-sm text-gray-700">Pour les plis et petits colis urgents</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <i className="ri-car-line text-teal-600 text-2xl mr-3"></i>
                          <strong className="text-gray-900">Véhicules légers</strong>
                        </div>
                        <p className="text-sm text-gray-700">Pour les colis moyens et multiples</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <i className="ri-truck-line text-teal-600 text-2xl mr-3"></i>
                          <strong className="text-gray-900">Utilitaires</strong>
                        </div>
                        <p className="text-sm text-gray-700">Pour les gros volumes et palettes</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <i className="ri-e-bike-line text-teal-600 text-2xl mr-3"></i>
                          <strong className="text-gray-900">Vélos cargo</strong>
                        </div>
                        <p className="text-sm text-gray-700">Pour le centre de Paris</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Suivi en temps réel</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Comme pour nos <Link to="/livraison-express-paris" className="text-teal-600 font-semibold hover:text-teal-700">livraisons express en semaine</Link>, vous bénéficiez d'un suivi GPS en temps réel de votre colis. Notifications automatiques à chaque étape : prise en charge, en cours de livraison, livré. Besoin d'aide pour choisir ? Lisez notre <Link to="/blog/choisir-transporteur-paris-guide" className="text-teal-600 font-semibold hover:text-teal-700">guide complet pour choisir son transporteur</Link>.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <a href="#tarifs-weekend" id="tarifs-weekend" className="hover:text-teal-600 transition-colors">
                  Tarifs et conditions du transport week-end
                </a>
              </h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                La question du prix revient souvent : combien coûte un <strong>transport urgent week-end à Paris</strong> ? Voici notre politique tarifaire transparente :
              </p>

              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-lg p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Grille tarifaire week-end</h3>
                
                <div className="space-y-6">
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xl font-bold text-gray-900">Coursier moto express</h4>
                      <span className="text-2xl font-bold text-teal-600">À partir de 35€</span>
                    </div>
                    <p className="text-gray-700 mb-2">Prise en charge sous 30 min • Livraison en 1h max</p>
                    <p className="text-sm text-gray-600">Supplément week-end : +15% (samedi) / +25% (dimanche et jours fériés)</p>
                  </div>

                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xl font-bold text-gray-900">Véhicule léger</h4>
                      <span className="text-2xl font-bold text-teal-600">À partir de 55€</span>
                    </div>
                    <p className="text-gray-700 mb-2">Prise en charge sous 1h • Capacité jusqu'à 500kg</p>
                    <p className="text-sm text-gray-600">Supplément week-end : +15% (samedi) / +25% (dimanche et jours fériés)</p>
                  </div>

                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xl font-bold text-gray-900">Utilitaire grand volume</h4>
                      <span className="text-2xl font-bold text-teal-600">À partir de 85€</span>
                    </div>
                    <p className="text-gray-700 mb-2">Prise en charge sous 2h • Capacité jusqu'à 1200kg</p>
                    <p className="text-sm text-gray-600">Supplément week-end : +15% (samedi) / +25% (dimanche et jours fériés)</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>💡 Astuce :</strong> Les tarifs sont dégressifs pour les courses multiples ou les contrats réguliers. Contactez-nous pour un devis personnalisé.
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-alert-line text-amber-600 mr-2"></i>
                  Pourquoi un supplément week-end ?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Le supplément week-end couvre les coûts supplémentaires : majoration salariale des coursiers, disponibilité permanente de l'équipe logistique, et maintien d'une flotte dédiée. Malgré ce supplément, nos tarifs restent très compétitifs pour un <strong>transport urgent dimanche</strong> ou samedi à Paris.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <a href="#cas-usage" id="cas-usage" className="hover:text-teal-600 transition-colors">
                  Cas d'usage réels : ils nous ont fait confiance
                </a>
              </h2>

              <div className="space-y-6">
                <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-start mb-4">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-restaurant-line text-3xl text-teal-600"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Restaurant gastronomique - Samedi 18h</h3>
                      <p className="text-sm text-gray-600 mb-3">Secteur : Restauration • Service : Coursier moto express</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>Situation :</strong> Un restaurant étoilé du 8ème arrondissement manque de homards frais pour le service du soir. Le fournisseur habituel est fermé.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Solution :</strong> Prise en charge chez un fournisseur de Rungis en 25 minutes, livraison au restaurant en 45 minutes. Service sauvé, clients satisfaits.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-start mb-4">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-heart-pulse-line text-3xl text-teal-600"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Cabinet médical - Dimanche 10h</h3>
                      <p className="text-sm text-gray-600 mb-3">Secteur : Santé • Service : Véhicule léger</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>Situation :</strong> Un cabinet de garde a besoin de médicaments spécifiques pour un patient en urgence. La pharmacie de garde est à 15km.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Solution :</strong> Notre <strong>coursier week-end</strong> récupère les médicaments et les livre en 35 minutes. Patient pris en charge rapidement. Découvrez tous nos <Link to="/coursier-paris" className="text-teal-600 font-semibold hover:text-teal-700">services coursier à Paris</Link>.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-start mb-4">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-cake-3-line text-3xl text-teal-600"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Organisateur d'événements - Samedi 14h</h3>
                      <p className="text-sm text-gray-600 mb-3">Secteur : Événementiel • Service : Utilitaire</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>Situation :</strong> Un mariage de 200 personnes : la décoration florale commandée n'est pas arrivée. Cérémonie à 16h.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Solution :</strong> Récupération chez le fleuriste en banlieue, livraison au lieu de réception en 1h30. Mariage sauvé, mariés ravis.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <a href="#commander" id="commander" className="hover:text-teal-600 transition-colors">
                  Comment commander un transport urgent le week-end ?
                </a>
              </h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Commander une <strong>livraison samedi dimanche Paris</strong> est simple et rapide :
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Appelez-nous</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Contactez notre centrale au <a href="tel:+33632410246" className="text-teal-600 font-semibold hover:text-teal-700">+33 6 32 41 02 46</a>. Un opérateur vous répond immédiatement, 24h/24.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Décrivez votre besoin</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Indiquez l'adresse de prise en charge, la destination, la nature du colis et le degré d'urgence.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                    3
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Validation et suivi</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Recevez un devis instantané, validez, et suivez votre colis en temps réel via SMS ou notre interface.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Informations à préparer pour gagner du temps</h3>
                <ul className="grid md:grid-cols-2 gap-3">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Adresses complètes (avec codes d'accès si nécessaire)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Dimensions et poids approximatifs du colis</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Nature de la marchandise (fragile, périssable, etc.)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Contacts des expéditeur et destinataire</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Créneau horaire souhaité (si applicable)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Instructions spéciales de livraison</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-8 md:p-12 text-white text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Une urgence ce week-end ? Nous sommes là !
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Service 24/7 • Prise en charge rapide • Tarifs transparents • Suivi en temps réel
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+33632410246"
                  className="inline-flex items-center justify-center bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Appelez maintenant : +33 6 32 41 02 46
                </a>
                <Link
                  to="/coursier-paris"
                  className="inline-flex items-center justify-center bg-teal-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-800 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Nos services coursier
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </section>

            {/* Price Simulator */}
            <div className="mb-12">
              <PriceSimulator />
            </div>

            {/* Related Articles */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Articles connexes</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/blog/${article.slug}`}
                    className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="relative w-full h-48">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                        {article.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </article>
      </div>

      <Footer />
    </>
  );
}
